import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { sendCopilotMessage } from '../CopilotPanel';
import { workItemsApi } from '../../api/workItems';
import { routinesApi } from '../../api/routines';
import { journalApi } from '../../api/journal';
import { invoicesApi } from '../../api/invoices';
import type { AttentionInboxItemDto } from '../../api/analytics';
import { CATEGORY_BADGE, SEVERITY_DOT } from './inboxShared';
import { formatMoney } from '../../utils/formatMoney';
import { formatApiError } from '../../utils/errors';
import { linkifyDescription } from '../../utils/linkifyDocNumbers';
import type { DrillView } from './drillView';

/**
 * InboxItemCard — the ACCOMPANY mechanism: a work item that answers
 * KAS (what) / KUR (where) / KODĖL DABAR (why now) / KAIP (how), with actions:
 * Tirti su AI (opens the buddy pre-loaded with the investigation), Klausti kliento
 * (queues a client question with the record linked), Atidėti iki, Ne problema,
 * Atlikta (manual done for what the system can't detect). Disposition actions
 * need a stableKey and are never offered on automated items.
 */

interface Props {
  item: AttentionInboxItemDto;
  onDrill: (view: DrillView) => void;
  defaultOpen?: boolean;
  aiEnabled?: boolean; // false = hide "Tirti su AI" (the question would vanish silently)
  lockOpen?: boolean;       // CASE MODE: the detail renders expanded, no collapsed row, no toggle — only a status ends it
  onResolved?: () => void;  // CASE MODE: a verdict was applied (Atlikta/Atidėti/Ne problema or a flow that retires the card)
  openCaseOnClick?: boolean; // list mode: the row click opens the CASE MODAL instead of inline expansion
  onOpenCase?: () => void;
}

export default function InboxItemCard({
  item, onDrill, defaultOpen = false, aiEnabled = false,
  lockOpen = false, onResolved, openCaseOnClick = false, onOpenCase,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [openToggled, setOpenToggled] = useState(defaultOpen);
  const open = lockOpen ? true : openToggled;
  const [postponeDate, setPostponeDate] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [statusChoice, setStatusChoice] = useState('');
  const [statusComment, setStatusComment] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const actionable = !!item.stableKey && !item.automated;
  const overdue = item.daysOverdue !== null && item.daysOverdue > 0;
  const isPremadeRoutine = item.agentName === 'Routines' && item.isPremade;
  const isMissedReminder = item.agentName === 'Routines' && item.alarmCode === 'missedPeriods';
  const isCatchupSummary = item.agentName === 'Routines'
    && item.catchupDrafts != null && item.catchupDrafts.length > 0;
  // MonthEndReadiness escalates the unclosed period to a WORK item once overdue —
  // the button opens the wizard on that exact month (the close is one flow away).
  const isCloseDue = item.agentName === 'MonthEndReadiness' && item.alarmCode === 'closeDue';
  // The year-close deadline card (ComplianceCalendar year walk) — same wizard CTA.
  const isAnnualCloseDue = item.agentName === 'ComplianceCalendar' && item.alarmCode === 'annualCloseDue';
  // Reminder-routine nudges carry their own CTA (the work lives one click away).
  const isBankImportDue = item.agentName === 'Routines' && item.alarmCode === 'bankImportDue';
  const isPeriodCloseDue = item.agentName === 'Routines' && item.alarmCode === 'periodCloseDue';
  const openCloseWizard = () => {
    const month = item.title.match(/\d{4}-\d{2}/)?.[0];
    navigate(month ? `/uzdarymas?month=${month}` : '/uzdarymas');
  };

  const catchUp = async () => {
    setActionError(null);
    try {
      await routinesApi.catchup(item.routineId ?? 0);
      // The reminder's job is done — retire it; the summary card (its own key) replaces it.
      if (item.routineId != null) {
        await workItemsApi
          .dispose(item.agentName, `routine:${item.routineId}:catchup`, 'ManualDone', { title: item.title })
          .catch(() => {});
      }
      await queryClient.invalidateQueries({ queryKey: ['analytics-inbox'] });
      await queryClient.invalidateQueries({ queryKey: ['work-item-dispositions'] });
      await queryClient.invalidateQueries({ queryKey: ['routines'] });
      onResolved?.();
    } catch (err) {
      setActionError(formatApiError(err));
    }
  };

  const confirmAllDrafts = async () => {
    setActionError(null);
    try {
      for (const d of item.catchupDrafts ?? []) {
        if (item.routineKind === 'RecurringInvoice') {
          await invoicesApi.post(d.journalEntryId, 'Made');
        } else {
          await journalApi.post(d.journalEntryId);
        }
        // Per-period keys dispose each confirmed draft individually.
        if (item.routineId != null) {
          await workItemsApi
            .dispose(item.agentName, `routine:${item.routineId}:${d.period}`, 'ManualDone', { title: `${item.title} (${d.period})` })
            .catch(() => {});
        }
      }
      // All posted → the summary card's own key retires with them (reached only if every post succeeded).
      if (item.routineId != null) {
        await workItemsApi
          .dispose(item.agentName, `routine:${item.routineId}:catchupDone`, 'ManualDone', { title: item.title })
          .catch(() => {});
      }
      await queryClient.invalidateQueries({ queryKey: ['analytics-inbox'] });
      await queryClient.invalidateQueries({ queryKey: ['work-item-dispositions'] });
      onResolved?.();
    } catch (err) {
      setActionError(formatApiError(err));
    }
  };

  const confirmAndPost = async () => {
    setActionError(null);
    try {
      if (item.routineKind === 'RecurringInvoice' && item.invoiceId != null) {
        await invoicesApi.post(item.invoiceId, 'Made');
      } else if (item.journalEntryId != null) {
        await journalApi.post(item.journalEntryId);
      } else {
        return;
      }
      if (item.stableKey) {
        await workItemsApi.dispose(item.agentName, item.stableKey, 'ManualDone', { title: item.title });
      }
      await queryClient.invalidateQueries({ queryKey: ['analytics-inbox'] });
      await queryClient.invalidateQueries({ queryKey: ['work-item-dispositions'] });
      await queryClient.invalidateQueries({ queryKey: ['routines'] });
      onResolved?.(); // premade draft confirmed — the case ends
    } catch (err) {
      setActionError(formatApiError(err));
    }
  };

  const disposeMutation = useMutation({
    mutationFn: (vars: Parameters<typeof workItemsApi.dispose>) => workItemsApi.dispose(...vars),
    onSuccess: async () => {
      setActionError(null);
      await queryClient.invalidateQueries({ queryKey: ['analytics-inbox'] });
      await queryClient.invalidateQueries({ queryKey: ['work-item-dispositions'] });
      onResolved?.(); // the case is ended by this verdict
    },
    onError: (err: Error) => setActionError(formatApiError(err)),
  });

  // Date helpers: the postpone default is TOMORROW (the common case) and the
  // steppers shift by whole days without opening the picker — never into the past.
  // The ISO string is the source of truth: anchor it to UTC ('T00:00:00Z') so
  // +1 day always changes the string — the old local-midnight parse fed through
  // toISOString() cancelled itself in EEST (addDays(d,1) === d; the 2026-09-10
  // "↑ arrow doesn't work" bug). The never-past floor uses the USER'S day.
  const localISO = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const addDays = (iso: string, n: number) => {
    const d = new Date(iso + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + n);
    const shifted = d.toISOString().slice(0, 10);
    const today = localISO(new Date());
    return shifted < today ? today : shifted; // ISO dates compare lexicographically
  };

  // The status verdict — ONE CLICK: choosing a status reveals its detail input
  // INLINE (the date pre-filled with tomorrow / the reason when the comment is
  // empty), and Patvirtinti applies the verdict directly. Never a confirm-of-a-confirm.
  const tomorrowISO = () => addDays(localISO(new Date()), 1);
  const applyStatus = () => {
    if (statusChoice === 'ManualDone') {
      disposeMutation.mutate([item.agentName, item.stableKey!, 'ManualDone',
        { title: item.title, reason: statusComment.trim() || undefined }]);
      setStatusChoice(''); setStatusComment('');
    } else if (statusChoice === 'Postponed') {
      const until = postponeDate || tomorrowISO();
      setPostponeDate(until);
      disposeMutation.mutate([item.agentName, item.stableKey!, 'Postponed',
        { until, title: item.title, reason: statusComment.trim() || undefined }]);
      setStatusChoice(''); setStatusComment('');
    } else if (statusChoice === 'Declined') {
      const reason = (statusComment.trim() || declineReason.trim());
      if (!reason) return; // the inline reason input is showing — Patvirtinti is disabled anyway
      disposeMutation.mutate([item.agentName, item.stableKey!, 'Declined',
        { title: item.title, reason }]);
      setStatusChoice(''); setStatusComment(''); setDeclineReason('');
    }
  };

  const investigate = () => {
    // Names and document numbers — the accountant thinks in those, not in ids.
    const ids = [
      item.personName ?? (item.personId ? t('analytics.inbox.card.askPerson', { id: item.personId }) : ''),
      item.documentNumber ?? (item.invoiceId ? t('analytics.inbox.card.askInvoice', { id: item.invoiceId }) : item.journalEntryId ? t('analytics.inbox.card.askJournal', { id: item.journalEntryId }) : ''),
    ].filter(Boolean).join(', ');
    const question = t('analytics.inbox.card.askTemplate', {
      title: item.title,
      ids,
    });
    // ASK IN PLACE: the chat docks beside the work (no navigation — the case
    // stays open, question pre-filled). Explanation and work side by side.
    sendCopilotMessage(question);
  };

  return (
    <div className={lockOpen ? '' : 'border border-gray-100 rounded-lg hover:border-gray-200 transition-colors'}>
      {/* Collapsed row — in list mode the click OPENS THE CASE (the modal); inline
          expansion remains for embedded contexts (the wizard's cleanup step). */}
      <button
        onClick={() => { if (openCaseOnClick) onOpenCase?.(); else setOpenToggled(prev => !prev); }}
        className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-primary-50/50 transition-colors"
      >
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${SEVERITY_DOT[item.severity ?? ''] ?? 'bg-gray-300'}`} />
        <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full flex-shrink-0 ${CATEGORY_BADGE[item.category] ?? 'bg-gray-100 text-gray-600'}`}>
          {t(`analytics.inbox.cat.${item.category}`)}
        </span>
        <span className="flex-1 text-sm text-gray-800 truncate">{item.title}</span>
        {item.amount != null && (
          <span className={`text-sm font-medium tabular-nums ${overdue ? 'text-red-600' : 'text-gray-700'}`}>
            {formatMoney(item.amount)}
          </span>
        )}
        {item.dueDate && (
          <span className="text-xs text-gray-400 flex-shrink-0 tabular-nums">
            {item.daysOverdue != null && item.daysOverdue > 0
              ? t('analytics.inbox.overdueBy', { days: item.daysOverdue })
              : new Date(item.dueDate).toLocaleDateString('lt-LT')}
          </span>
        )}
        <svg
          className={`w-4 h-4 text-gray-300 flex-shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
          fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Expanded: KAS / KUR / KODĖL DABAR / KAIP */}
      {open && (
        <div className="px-4 pb-3 pt-1 space-y-3 border-t border-gray-50">
          {/* KAS */}
          {item.description && (
            <div>
              <p className="text-[10px] uppercase font-semibold text-gray-400">{t('analytics.inbox.card.what')}</p>
              <p className="text-sm text-gray-700">
                {linkifyDescription(item.description, onDrill, token =>
                  setActionError(t('analytics.inbox.card.docNotFound', { token })))}
              </p>
            </div>
          )}

          {/* KUR */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase font-semibold text-gray-400">{t('analytics.inbox.card.where')}</span>
            {item.invoiceId != null && (
              <button
                onClick={() => onDrill({ kind: 'invoice', invoiceId: item.invoiceId! })}
                className="px-2 py-0.5 text-xs text-primary-700 bg-primary-50 border border-primary-200 rounded-full hover:border-primary-400"
              >
                {item.documentNumber ? `${t('analytics.inbox.card.toInvoice')}: ${item.documentNumber}` : `${t('analytics.inbox.card.toInvoice')} #${item.invoiceId}`}
              </button>
            )}
            {item.journalEntryId != null && (
              <button
                onClick={() => onDrill({ kind: 'journal', journalEntryId: item.journalEntryId! })}
                className="px-2 py-0.5 text-xs text-primary-700 bg-primary-50 border border-primary-200 rounded-full hover:border-primary-400"
              >
                {item.documentNumber ? `${t('analytics.inbox.card.toJournal')}: ${item.documentNumber}` : `${t('analytics.inbox.card.toJournal')} #${item.journalEntryId}`}
              </button>
            )}
            {item.personId != null && (
              <button
                onClick={() => onDrill({ kind: 'customer', personId: item.personId! })}
                className="px-2 py-0.5 text-xs text-primary-700 bg-primary-50 border border-primary-200 rounded-full hover:border-primary-400"
              >
                {item.personName ? `${t('analytics.inbox.card.toPerson')}: ${item.personName}` : `${t('analytics.inbox.card.toPerson')} #${item.personId}`}
              </button>
            )}
          </div>

          {/* KAIP */}
          <div>
            {item.suggestedAction && (
              <p className="text-xs text-gray-600 mb-2">
                <span className="font-semibold">{t('analytics.inbox.card.how')}:</span> {item.suggestedAction}
              </p>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              {isPremadeRoutine && !isCatchupSummary && (
                <button
                  onClick={confirmAndPost}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                >
                  ✓ {t('analytics.inbox.card.confirmAndPost')}
                </button>
              )}
              {isAnnualCloseDue && (
                <button
                  onClick={openCloseWizard}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                >
                  {t('analytics.inbox.card.openWizard')} →
                </button>
              )}
              {isBankImportDue && (
                <button
                  onClick={() => navigate('/banking/import')}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700"
                >
                  {t('analytics.inbox.card.openBankImport')} →
                </button>
              )}
              {isPeriodCloseDue && (
                <button
                  onClick={openCloseWizard}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700"
                >
                  {t('analytics.inbox.card.openWizard')} →
                </button>
              )}
              {isCloseDue && (
                <button
                  onClick={openCloseWizard}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700"
                >
                  {t('analytics.inbox.card.openWizard')} →
                </button>
              )}
              {isMissedReminder && (
                <button
                  onClick={catchUp}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700"
                >
                  ↩ {t('analytics.inbox.card.catchUp', { count: item.missedPeriods?.length ?? 0 })}
                </button>
              )}
              {isCatchupSummary && (
                <button
                  onClick={confirmAllDrafts}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                >
                  ✓✓ {t('analytics.inbox.card.confirmAll')}
                </button>
              )}
              {aiEnabled && (
                <button
                  onClick={investigate}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700"
                >
                  🔍 {t('analytics.inbox.card.investigate')}
                </button>
              )}
            </div>
            {/* STATUS — the verdict control, right-aligned and compact: pick the
                outcome, Patvirtinti applies it. It IS a status field, not buttons. */}
            {actionable && (
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <input
                  value={statusChoice === 'Declined' && !statusComment.trim() ? declineReason : statusComment}
                  onChange={e => statusChoice === 'Declined' && !statusComment.trim()
                    ? setDeclineReason(e.target.value)
                    : setStatusComment(e.target.value)}
                  placeholder={statusChoice === 'Declined' && !statusComment.trim()
                    ? t('analytics.inbox.card.declinePlaceholder')
                    : t('analytics.inbox.card.commentLabel')}
                  className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg flex-1 min-w-[180px]"
                />
                <select
                  value={statusChoice}
                  onChange={e => {
                    setStatusChoice(e.target.value);
                    if (e.target.value === 'Postponed' && !postponeDate)
                      setPostponeDate(tomorrowISO());
                  }}
                  aria-label={t('analytics.inbox.card.status.label')}
                  className="px-2 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded-lg hover:border-gray-500"
                >
                  <option value="">{t('analytics.inbox.card.status.label')}</option>
                  <option value="ManualDone">✓ {t('analytics.inbox.card.done')}</option>
                  <option value="Postponed">⏱ {t('analytics.inbox.card.postpone')}</option>
                  <option value="Declined">{t('analytics.inbox.card.notProblem')}</option>
                </select>
                {statusChoice === 'Postponed' && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => postponeDate && setPostponeDate(addDays(postponeDate, -1))}
                      disabled={!postponeDate || postponeDate <= localISO(new Date())}
                      aria-label={t('analytics.inbox.card.dayEarlier')}
                      className="px-2 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:border-gray-500 disabled:opacity-30"
                    >
                      ↓
                    </button>
                    <input
                      type="date"
                      value={postponeDate}
                      onChange={e => setPostponeDate(e.target.value)}
                      className="border border-gray-200 rounded-lg px-2 py-1 text-xs"
                    />
                    <button
                      onClick={() => setPostponeDate(addDays(postponeDate || localISO(new Date()), 1))}
                      aria-label={t('analytics.inbox.card.dayLater')}
                      className="px-2 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:border-gray-500"
                    >
                      ↑
                    </button>
                  </div>
                )}
                <button
                  onClick={applyStatus}
                  disabled={!statusChoice || (statusChoice === 'Postponed' && !postponeDate)
                    || (statusChoice === 'Declined' && !statusComment.trim() && !declineReason.trim())}
                  className="px-3 py-1.5 text-xs font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40"
                >
                  {t('analytics.inbox.card.status.apply')}
                </button>
              </div>
            )}
            {actionError && <p className="text-xs text-red-600 mt-1">{actionError}</p>}
          </div>

          {/* Catch-up summary: the per-period drafts, each individually confirmable */}
          {isCatchupSummary && (
            <div className="border-t border-gray-50 pt-2 space-y-1">
              {(item.catchupDrafts ?? []).map(d => (
                <div key={d.journalEntryId} className="flex items-center gap-3 px-2 py-1.5 rounded-lg bg-green-50/40">
                  <span className="text-xs font-mono text-gray-600 tabular-nums">{d.period}</span>
                  <span className="flex-1 text-sm text-gray-800 tabular-nums">{formatMoney(d.amount)}</span>
                  <button
                    onClick={() => onDrill(
                      item.routineKind === 'RecurringInvoice'
                        ? { kind: 'invoice', invoiceId: d.journalEntryId }
                        : { kind: 'journal', journalEntryId: d.journalEntryId })}
                    className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                  >
                    {item.routineKind === 'RecurringInvoice'
                      ? t('analytics.inbox.card.toInvoice')
                      : t('analytics.inbox.card.toJournal')} #{d.journalEntryId}
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        if (item.routineKind === 'RecurringInvoice') await invoicesApi.post(d.journalEntryId, 'Made');
                        else await journalApi.post(d.journalEntryId);
                        await queryClient.invalidateQueries({ queryKey: ['analytics-inbox'] });
                      } catch (err) {
                        setActionError(formatApiError(err));
                      }
                    }}
                    className="px-2.5 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:border-green-400"
                  >
                    ✓ {t('analytics.inbox.card.confirmAndPost')}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Postpone inline form */}

          {/* Decline inline form */}
        </div>
      )}
    </div>
  );
}
